import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  List, X, Layout, User, CaretDown, House, Info, Compass, Images, CurrencyDollar, ChatCircleDots, UsersThree, Medal,
  Question, FileText, ShieldCheck, LockKey, Certificate, BoundingBox, Ruler, HouseLine, Notebook, Mountains, MapTrifold,
  SquaresFour, GridFour, Waves, Hammer, Buildings, Plugs, Path, Crosshair, Drop, ListBullets, MapPin, Package, Tag,
  Calculator, RocketLaunch, Envelope, Lifebuoy, Article,
} from '@phosphor-icons/react';
import { MarketsMenu, MarketsMobile } from './MarketsMenu';
import { useAuth } from '../contexts/AuthContext';

const ICONS = {
  Home: House, About: Info, Services: Compass, Portfolio: Images, Pricing: CurrencyDollar, Contact: ChatCircleDots,
  'About Us': UsersThree, 'Why Choose Us': Medal, FAQ: Question, Blog: Article, 'Terms & Conditions': FileText, 'Privacy Policy': ShieldCheck,
  Security: LockKey, 'ALTA/NSPS Survey': Certificate, 'Boundary Survey': BoundingBox, 'Metes & Bounds': Ruler,
  'Mortgage Survey': HouseLine, 'Record of Survey': Notebook, 'Topographic Survey': Mountains, 'Site Plan Survey': MapTrifold,
  'Plot Plan Drafting': SquaresFour, 'Final Plat / Subdivision': GridFour, 'FEMA Elevation Certificate': Waves,
  'Construction Staking': Hammer, 'As-Built Survey': Buildings, 'Utility & Easement': Plugs, 'Right-of-Way / Route': Path,
  'Control Survey': Crosshair, 'Hydrographic Survey': Drop, 'All Services': ListBullets, 'Service Areas — 50 States': MapPin,
  'Portfolio Gallery': Images, 'Request Samples': Package, 'Pricing Plans': Tag, 'Quote Calculator': Calculator,
  'Get Started': RocketLaunch, 'Contact Us': Envelope, 'Customer Support': Lifebuoy,
};

const NavIcon = ({ name, size = 16, className = '' }) => {
  const Icon = ICONS[name];
  return Icon ? <Icon size={size} weight="duotone" className={className} /> : null;
};

// Mega-menu structure. Each item is either a plain link (no `columns`) or a dropdown.
const MENU = [
  { name: 'Home', path: '/' },
  {
    name: 'About',
    path: '/about',
    columns: [
      {
        heading: 'Company',
        links: [
          { name: 'About Us', path: '/about', desc: 'Who we are & our mission' },
          { name: 'Why Choose Us', path: '/about#why-us', desc: 'What sets us apart' },
          { name: 'FAQ', path: '/faq', desc: 'Frequently asked questions' },
          { name: 'Blog', path: '/blog', desc: 'Buyer questions answered' },
        ],
      },
      {
        heading: 'Policies',
        links: [
          { name: 'Terms & Conditions', path: '/terms' },
          { name: 'Privacy Policy', path: '/privacy' },
          { name: 'Security', path: '/security' },
        ],
      },
    ],
  },
  {
    name: 'Services',
    path: '/services',
    wide: true,
    columns: [
      {
        heading: 'Boundary & Title',
        links: [
          { name: 'ALTA/NSPS Survey', path: '/services/alta-nsps' },
          { name: 'Boundary Survey', path: '/services/boundary' },
          { name: 'Metes & Bounds', path: '/services/metes-bounds' },
          { name: 'Mortgage Survey', path: '/services/mortgage-survey' },
          { name: 'Record of Survey', path: '/services/record-of-survey' },
        ],
      },
      {
        heading: 'Topographic & Site',
        links: [
          { name: 'Topographic Survey', path: '/services/topographic' },
          { name: 'Site Plan Survey', path: '/services/site-plan' },
          { name: 'Plot Plan Drafting', path: '/services/plot-plans' },
          { name: 'Final Plat / Subdivision', path: '/services/final-plats' },
          { name: 'FEMA Elevation Certificate', path: '/services/elevation-certificate' },
        ],
      },
      {
        heading: 'Construction & Infra',
        links: [
          { name: 'Construction Staking', path: '/services/construction-staking' },
          { name: 'As-Built Survey', path: '/services/as-built' },
          { name: 'Utility & Easement', path: '/services/utility' },
          { name: 'Right-of-Way / Route', path: '/services/right-of-way' },
          { name: 'Control Survey', path: '/services/control-survey' },
          { name: 'Hydrographic Survey', path: '/services/hydrographic' },
        ],
      },
      {
        heading: 'Coverage',
        accent: true,
        links: [
          { name: 'All Services', path: '/services', desc: 'Browse full catalog' },
          { name: 'Service Areas — 50 States', path: '/service-areas', desc: 'Counties & standards' },
        ],
      },
    ],
  },
  {
    name: 'Portfolio',
    path: '/portfolio',
    columns: [
      {
        heading: 'Our Work',
        links: [
          { name: 'Portfolio Gallery', path: '/portfolio', desc: 'Browse past projects' },
          { name: 'Request Samples', path: '/samples', desc: 'Get anonymized deliverables' },
        ],
      },
    ],
  },
  {
    name: 'Pricing',
    path: '/pricing',
    columns: [
      {
        heading: 'Pricing & Estimates',
        links: [
          { name: 'Pricing Plans', path: '/pricing', desc: 'Hourly, project, retainer' },
          { name: 'Quote Calculator', path: '/quote', desc: 'Instant hourly estimate' },
          { name: 'Get Started', path: '/get-started', desc: 'Upload files & brief us' },
        ],
      },
    ],
  },
  {
    name: 'Contact',
    path: '/contact',
    columns: [
      {
        heading: 'Reach Us',
        links: [
          { name: 'Contact Us', path: '/contact', desc: 'General inquiries' },
          { name: 'Customer Support', path: '/support', desc: 'Open a support ticket' },
          { name: 'Get Started', path: '/get-started', desc: 'Start a new project' },
        ],
      },
    ],
  },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const closeTimer = useRef();
  const location = useLocation();
  const { user } = useAuth();

  const handleEnter = (i) => { clearTimeout(closeTimer.current); setOpenIdx(i); };
  const handleLeave = () => { closeTimer.current = setTimeout(() => setOpenIdx(null), 120); };

  useEffect(() => { setOpenIdx(null); setMobileOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  return (
    <header className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="navbar">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Left - Logo + Desktop Menu */}
          <div className="flex items-center gap-4 xl:gap-8 flex-1">
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={24} /> : <List size={24} />}
            </button>

            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
              <Layout size={28} weight="bold" className="text-[#3B82F6]" />
              <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Outfit' }}>
                <span className="text-[#3B82F6]">Surveys</span>
                <span className="text-[#8B5CF6]">Maps</span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {MENU.map((item, i) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.columns && handleEnter(i)}
                  onMouseLeave={handleLeave}
                  onFocus={() => item.columns && handleEnter(i)}
                >
                  <Link
                    to={item.path}
                    data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`inline-flex items-center gap-1.5 px-2 xl:px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item.path) ? 'text-[#3B82F6]' : 'text-zinc-700 hover:text-[#3B82F6]'
                    }`}
                  >
                    <NavIcon name={item.name} className="opacity-80 hidden xl:inline" />
                    {item.name}
                    {item.columns && <CaretDown size={12} weight="bold" className={`transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />}
                  </Link>

                  {item.columns && openIdx === i && (
                    <div
                      className={`absolute left-0 top-full pt-2 ${item.wide ? 'w-[600px] xl:w-[760px]' : 'w-[300px]'}`}
                      data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="bg-white border border-zinc-200 rounded-xl shadow-2xl p-6">
                        <div className={`grid gap-6 ${item.wide ? 'grid-cols-2 xl:grid-cols-4' : 'grid-cols-' + item.columns.length}`}>
                          {item.columns.map((col) => (
                            <div key={col.heading}>
                              <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-3 ${col.accent ? 'text-[#06D6E0]' : 'text-zinc-400'}`}>
                                {col.heading}
                              </h4>
                              <ul className="space-y-2">
                                {col.links.map((l) => (
                                  <li key={l.path}>
                                    <Link
                                      to={l.path}
                                      data-testid={`dropdown-link-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                      className="group flex items-start gap-2.5 py-1"
                                    >
                                      <NavIcon name={l.name} size={18} className="text-zinc-400 group-hover:text-[#3B82F6] transition-colors flex-shrink-0 mt-0.5" />
                                      <span>
                                        <span className="block text-sm font-semibold text-zinc-900 group-hover:text-[#3B82F6] transition-colors">
                                          {l.name}
                                        </span>
                                        {l.desc && <span className="block text-xs text-zinc-500 mt-0.5">{l.desc}</span>}
                                      </span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right - Auth + CTA */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {user ? (
              <Link
                to={user.role === 'admin' ? '/admin/overview' : '/dashboard'}
                data-testid="nav-dashboard-link"
                className="hidden md:flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors px-3 py-2 border border-zinc-200 hover:border-zinc-300 rounded-md"
              >
                <User size={18} />
                <span className="hidden lg:inline">{user.role === 'admin' ? 'Admin' : 'Projects'}</span>
              </Link>
            ) : (
              <Link to="/login" data-testid="nav-login-link" className="hidden md:block text-sm text-zinc-600 hover:text-zinc-900 transition-colors">
                Sign In
              </Link>
            )}
            <div className="relative">
              <div className="absolute bottom-full inset-x-0 mb-0.5"><MarketsMenu /></div>
              <Link to="/get-started" data-testid="nav-cta-button" className="btn-primary px-4 py-2 text-sm text-center">
                Get Started
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu with collapsible submenus */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-zinc-200 py-2 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu" onClick={(e) => { if (e.target.closest('a')) { setMobileOpen(false); setMobileExpanded({}); } }}>
            {MENU.map((item) => (
              <div key={item.name} className="border-b border-zinc-100 last:border-b-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.path}
                    data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex-1 flex items-center gap-2 py-3 px-4 text-sm font-medium ${isActive(item.path) ? 'text-[#3B82F6]' : 'text-zinc-800'}`}
                  >
                    <NavIcon name={item.name} size={18} />
                    {item.name}
                  </Link>
                  {item.columns && (
                    <button
                      onClick={() => setMobileExpanded(p => ({ ...p, [item.name]: !p[item.name] }))}
                      className="p-3 text-zinc-500"
                      data-testid={`mobile-expand-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-label={`Expand ${item.name}`}
                    >
                      <CaretDown size={14} weight="bold" className={`transition-transform ${mobileExpanded[item.name] ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {item.columns && mobileExpanded[item.name] && (
                  <div className="bg-zinc-50 pb-3">
                    {item.columns.map((col) => (
                      <div key={col.heading} className="px-4 pt-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">{col.heading}</p>
                        {col.links.map((l) => (
                          <Link
                            key={l.path}
                            to={l.path}
                            data-testid={`mobile-dropdown-link-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className="flex items-center gap-2 py-1.5 pl-2 text-sm text-zinc-700 hover:text-[#3B82F6]"
                          >
                            <NavIcon name={l.name} className="text-zinc-400" />
                            {l.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <MarketsMobile />
            <div className="px-4 pt-4 border-t border-zinc-200 mt-2">
              {user ? (
                <Link to={user.role === 'admin' ? '/admin/overview' : '/dashboard'} className="block text-sm text-zinc-600 py-2">
                  {user.role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}
                </Link>
              ) : (
                <Link to="/login" className="block text-sm text-zinc-500 py-2">Sign In</Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
